import React, { useEffect, useState } from "react";
import {
  FormControl,
  Box,
  Text,
} from "@primer/react";
import moment from "moment";
import { NotifyHelper } from "../../../helpers/toast";
import { chamSocKhachHangApi } from "../../../api/chamSocKhachHangApi";
import { hocSinhApi } from "../../../api/hocSinhApi";
import { phuHuynhApi } from "../../../api/phuHuynhApi";
import { IPhuHuynh } from "../../../models/response/phu-huynh/IPhuHuynhItemRespone";
import Button from "../../ui/button";
import SelectBoxSingle from "../../selectbox-single";
import SelectBoxLoaiLienHe from "../../selectbox-loai-lien-he";
import StyledDatePicker from "../../react-datepicker-style/ReactDatePicker";
import { useLoaiLienHe } from "../../selectbox-loai-lien-he/useLoaiLienHe";
import SelectBoxKetQuaLienHe from "../../selectbox-ket-qua-lien-he";
import { useKetQuaLienHe } from "../../selectbox-ket-qua-lien-he/useKetQuaLienHe";
import { khoLeadApi } from "../../../api/khoLeadApi";
import { sectionLabelSx, textareaSx } from "../../../pages/crm-survey/KhoLead/khoLeadStyles";

// Interfaces
interface CSKHFormProps {
  ts_phuhuynh_id: number;
  onSuccess?: () => void;
  onGhiDanhSuccess?: () => void;
  isHideGhiDanh?: boolean;
  // compact: một ô nhập, bấm vào mới mở; boxed: khung đầy đủ như Kho Lead
  variant?: "compact" | "boxed";
  // Chế độ Kho Lead: lưu thành tương tác của Lead (API Kho Lead), lấy danh mục theo cơ sở của Lead
  lead?: { id: number; dm_coso_id: number };
}

interface HocSinh {
  id: number;
  ho_ten: string;
}

interface FormErrors {
  noiDung?: string;
  loaiLienHeId?: string;
  optionSelectedId?: string;
  hocSinhSelectedId?: string;
  henXuLy?: string;
}

interface FormState {
  isExpanded: boolean;
  noiDung: string;
  optionSelectedId: number;
  hocSinhSelectedId: number;
  isDone: boolean;
  henXuLy: string;
  isLoading: boolean;
  loaiLienHeId: number;
  ketQuaLienHeId: number;
  ghiChu: string;
}

const DEFAULT_HOC_SINH: HocSinh = {
  id: 0,
  ho_ten: "Không gán vào học sinh",
};

const INITIAL_FORM_STATE: FormState = {
  isExpanded: false,
  noiDung: "",
  optionSelectedId: 1,
  hocSinhSelectedId: 0,
  isDone: false,
  henXuLy: "",
  isLoading: false,
  loaiLienHeId: 0,
  ketQuaLienHeId: 0,
  ghiChu: "",
};

const CSKHForm: React.FC<CSKHFormProps> = ({
  ts_phuhuynh_id,
  onSuccess,
  variant = "compact",
  lead,
}) => {
  // States
  const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE);
  const [hocSinhs, setHocSinhs] = useState<HocSinh[]>([DEFAULT_HOC_SINH]);
  const [phuHuynh, setPhuHuynh] = useState<IPhuHuynh>();
  const dmCosoId = lead?.dm_coso_id ?? phuHuynh?.dm_coso_id;
  const loaiLienHes = useLoaiLienHe(dmCosoId);
  const ketQuaLienHes = useKetQuaLienHe(dmCosoId);
  // Cần ngày hẹn khi loại liên hệ hoặc kết quả liên hệ được cấu hình "Cần ngày hẹn", giống Kho Lead
  const needNgayHen = !!loaiLienHes.find(x => x.id === formState.loaiLienHeId)?.co_ngay_hen
    || !!ketQuaLienHes.find(x => x.id === formState.ketQuaLienHeId)?.co_ngay_hen;
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Validation Functions
  const validateNoiDung = (value: string): string | undefined => {
    if (!value.trim()) return "Nội dung chăm sóc là bắt buộc";
    if (value.length < 10) return "Nội dung chăm sóc phải có ít nhất 10 ký tự";
    if (value.length > 2500) return "Nội dung chăm sóc không được vượt quá 2500 ký tự";
    return undefined;
  };

  const validateHenXuLy = (value: string): string | undefined => {
    if (needNgayHen && !value) {
      return "Loại liên hệ này cần chọn ngày hẹn";
    }
    if (value) {
      const selectedDate = moment(value);
      if (!selectedDate.isValid()) return "Ngày không hợp lệ";
      if (selectedDate.isBefore(moment().startOf("day"))) {
        return "Ngày hẹn xử lý không thể là ngày trong quá khứ";
      }
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    const noiDungError = validateNoiDung(formState.noiDung);
    if (noiDungError) newErrors.noiDung = noiDungError;

    if (needNgayHen) {
      const henXuLyError = validateHenXuLy(formState.henXuLy);
      if (henXuLyError) newErrors.henXuLy = henXuLyError;
    }

    // Tương tác Lead bắt buộc có loại liên hệ (backend Kho Lead kiểm tra)
    if (lead && !formState.loaiLienHeId) newErrors.loaiLienHeId = "Vui lòng chọn loại liên hệ";

    setErrors(newErrors);
    setTouched({
      noiDung: true,
      henXuLy: true,
      loaiLienHeId: true,
    });

    return Object.keys(newErrors).length === 0;
  };

  // API Functions
  const fetchData = async () => {
    if (!ts_phuhuynh_id) return;
    
    try {
      setFormState(prev => ({ ...prev, isLoading: true }));
      
      // Load phụ huynh data
      const phRes = await phuHuynhApi.getById(ts_phuhuynh_id);
      if (phRes.is_success) setPhuHuynh(phRes.data);
      
      // Load học sinh data
      const hsRes = await hocSinhApi.SelectByParent(ts_phuhuynh_id);
      if (hsRes.is_success) {
        const hocSinhOptions = hsRes.data.map((element: any) => ({
          id: element.id,
          ho_ten: `Là yêu cầu của ${element.ho_ten}`,
        }));
        setHocSinhs([DEFAULT_HOC_SINH, ...hocSinhOptions]);
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi khi tải dữ liệu");
    } finally {
      setFormState(prev => ({ ...prev, isLoading: false }));
    }
  };

  // Event Handlers
  const handleSubmit = async () => {
    if (!validateForm()) {
      NotifyHelper.Error("Vui lòng kiểm tra lại thông tin");
      return;
    }

    try {
      setFormState(prev => ({ ...prev, isLoading: true }));

      if (lead) {
        const leadRes = await khoLeadApi.addInteraction(lead.id, {
          dm_loai_lien_he_id: formState.loaiLienHeId,
          // API Kho Lead nhận tên kết quả liên hệ
          result: ketQuaLienHes.find(x => x.id === formState.ketQuaLienHeId)?.ten_ket_qua,
          content: formState.noiDung.trim(),
          appointment: needNgayHen && formState.henXuLy ? formState.henXuLy : undefined,
          note: formState.ghiChu.trim() || undefined,
        });
        if (leadRes.is_success) {
          NotifyHelper.Success("Đã lưu lịch sử chăm sóc");
          resetForm();
          onSuccess?.();
        } else {
          NotifyHelper.Error(leadRes.message || "Có lỗi xảy ra khi thêm mới");
        }
        return;
      }

      const formattedHenXuLy = formState.henXuLy
        ? moment(formState.henXuLy).format("YYYY-MM-DD")
        : null;
        
      const formattedNgayXuLy = formState.isDone
        ? moment().format("YYYY-MM-DD")
        : null;
        
      const ngayHenTraLoiText = formState.henXuLy 
        ? moment(formState.henXuLy).format("DD/MM/YYYY")
        : "";
  
      const requestData = {
        id: 0,
        dien_thoai: phuHuynh?.dien_thoai || "",
        ten_khach_hang: phuHuynh?.ho_ten || "",
        noi_dung: formState.noiDung.trim(),
        ts_hocsinh_id: formState.hocSinhSelectedId,
        da_xu_ly: formState.isDone,
        ngay_xu_ly: formattedNgayXuLy,
        user_xu_ly_id: 0,
        ghi_chu: formState.ghiChu.trim(),
        ts_phuhuynh_id,
        ts_hoso_survey_id: 0,
        ket_qua_xu_ly: "",
        is_request: formState.optionSelectedId === 2,
        ngay_hen_tra_loi: formattedHenXuLy,
        ngayHenTraLoiText: ngayHenTraLoiText,
        tsPhuHuynhAdmStatusId: phuHuynh?.ts_phuhuynh_adm_status_id || 0,
        dm_loai_lien_he_id: formState.loaiLienHeId || null,
        dm_ket_qua_lien_he_id: formState.ketQuaLienHeId || null,
      };

      const res = await chamSocKhachHangApi.insert(requestData);

      if (res.is_success) {
        NotifyHelper.Success("Thêm mới thành công");
        resetForm();
        onSuccess?.();
      } else {
        NotifyHelper.Error("Có lỗi xảy ra khi thêm mới");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi xảy ra khi gửi yêu cầu");
    } finally {
      setFormState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleChange = (field: keyof FormState, value: any) => {
    setFormState(prev => ({ ...prev, [field]: value }));
    
    if (touched[field]) {
      let error: string | undefined;
      if (field === 'noiDung') error = validateNoiDung(value);
      if (field === 'henXuLy') error = validateHenXuLy(value);
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const resetForm = () => {
    setFormState(INITIAL_FORM_STATE);
    setErrors({});
    setTouched({});
  };

  // Effects
  useEffect(() => {
    fetchData();
  }, [ts_phuhuynh_id]);


  const { isExpanded } = formState;
  // Ngày hẹn chỉ theo cấu hình "Cần ngày hẹn" của loại / kết quả liên hệ
  const showNgayHen = needNgayHen;

  // Các trường dùng chung cho 2 kiểu hiển thị
  const fieldLoaiLienHe = (
    <FormControl>
      <FormControl.Label>
        Loại liên hệ{lead && <Box as="span" sx={{ color: "danger.fg" }}> *</Box>}
      </FormControl.Label>
      <SelectBoxLoaiLienHe
        loaiLienHes={loaiLienHes}
        value={formState.loaiLienHeId}
        onValueChanged={v => {
          handleChange("loaiLienHeId", v ?? 0);
          if (v) setErrors(prev => ({ ...prev, loaiLienHeId: undefined }));
        }}
        width="100%"
      />
      {touched.loaiLienHeId && errors.loaiLienHeId && (
        <FormControl.Validation variant="error">{errors.loaiLienHeId}</FormControl.Validation>
      )}
    </FormControl>
  );
  const fieldGhiChu = (
    <FormControl>
      <FormControl.Label>Ghi chú thêm</FormControl.Label>
      <Box
        as="textarea"
        value={formState.ghiChu}
        onChange={(e: any) => handleChange("ghiChu", e.target.value)}
        sx={{ ...textareaSx, minHeight: "64px" }}
      />
    </FormControl>
  );
  const fieldKetQua = (
    <FormControl>
      <FormControl.Label>Kết quả liên hệ</FormControl.Label>
      <SelectBoxKetQuaLienHe
        ketQuaLienHes={ketQuaLienHes}
        value={formState.ketQuaLienHeId}
        onValueChanged={v => handleChange("ketQuaLienHeId", v ?? 0)}
        width="100%"
      />
    </FormControl>
  );
  const fieldHocSinh = !ts_phuhuynh_id ? null : (
    <FormControl>
      <FormControl.Label>Học sinh</FormControl.Label>
      <SelectBoxSingle
        items={hocSinhs.map(h => ({ id: h.id, text: h.ho_ten }))}
        value={formState.hocSinhSelectedId}
        onValueChanged={v => handleChange("hocSinhSelectedId", Number(v ?? 0))}
        disabled={formState.isLoading}
        width="100%"
      />
    </FormControl>
  );
  const fieldNgayHen = showNgayHen ? (
    <FormControl>
      <FormControl.Label>Ngày hẹn xử lý</FormControl.Label>
      <StyledDatePicker
        selected={formState.henXuLy ? moment(formState.henXuLy).toDate() : null}
        onChange={(date: Date | null) => handleChange("henXuLy", date ? moment(date).format("YYYY-MM-DD") : "")}
        onBlur={() => handleBlur("henXuLy")}
        placeholderText="dd/mm/yyyy"
      />
      {touched.henXuLy && errors.henXuLy && (
        <FormControl.Validation variant="error">{errors.henXuLy}</FormControl.Validation>
      )}
    </FormControl>
  ) : null;

  // Kiểu khung giống "Thêm tương tác mới" bên Kho Lead: luôn mở đủ trường
  if (variant === "boxed") {
    return (
      <Box sx={{ border: "1px solid", borderColor: "border.default", borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{
          px: 3, py: 2, bg: "canvas.subtle",
          borderBottom: "1px solid", borderColor: "border.default",
          ...sectionLabelSx,
        }}>
          Thêm lịch sử chăm sóc
        </Box>
        <Box sx={{ p: 3, display: "grid", gap: 3 }}>
          {/* Ngày hẹn đứng ngay sau Loại liên hệ khi cấu hình cần ngày hẹn */}
          <Box sx={{ display: "grid", gridTemplateColumns: ["1fr", "1fr 1fr"], gap: 3 }}>
            {fieldLoaiLienHe}
            {fieldNgayHen}
            {fieldKetQua}
            {fieldHocSinh}
          </Box>
          <FormControl>
            <FormControl.Label>Nội dung<Box as="span" sx={{ color: "danger.fg" }}> *</Box></FormControl.Label>
            <Box
              as="textarea"
              value={formState.noiDung}
              onChange={(e: any) => handleChange('noiDung', e.target.value)}
              onBlur={() => handleBlur("noiDung")}
              sx={{
                ...textareaSx,
                minHeight: "72px",
                borderColor: touched.noiDung && errors.noiDung ? "danger.emphasis" : textareaSx.borderColor,
              }}
            />
            {touched.noiDung && errors.noiDung && (
              <FormControl.Validation variant="error">{errors.noiDung}</FormControl.Validation>
            )}
          </FormControl>
          {fieldGhiChu}
          <Box>
            <Button text="Lưu" variant="primary" isLoading={formState.isLoading} onClick={handleSubmit} />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      border: "1px solid", borderRadius: 2, bg: "canvas.default",
      borderColor: isExpanded ? "accent.emphasis" : "border.default",
      boxShadow: isExpanded ? "0 0 0 3px rgba(9, 105, 218, 0.15)" : undefined,
      transition: "border-color .15s, box-shadow .15s",
    }}>
      {/* Thu gọn: chỉ một ô nhập, bấm vào mới mở các trường còn lại */}
      <FormControl sx={{ p: 2 }}>
        <FormControl.Label visuallyHidden>Nội dung chăm sóc</FormControl.Label>
        <Box
          as="textarea"
          rows={isExpanded ? 3 : 1}
          placeholder="Thêm ghi chú hoặc yêu cầu chăm sóc…"
          value={formState.noiDung}
          onFocus={() => setFormState(prev => ({ ...prev, isExpanded: true }))}
          onChange={(e: any) => handleChange('noiDung', e.target.value)}
          onBlur={() => handleBlur("noiDung")}
          sx={{
            ...textareaSx,
            width: "100%",
            border: 0, boxShadow: "none", bg: "transparent", resize: isExpanded ? "vertical" : "none",
            minHeight: isExpanded ? "72px" : "32px",
            "&:focus, &:focus-visible": { outline: "none", boxShadow: "none" },
          }}
        />
        {touched.noiDung && errors.noiDung && (
          <FormControl.Validation variant="error">{errors.noiDung}</FormControl.Validation>
        )}
      </FormControl>

      {isExpanded && (
        <>
          <Box sx={{
            px: 3, pb: 3, display: "grid", gap: 3,
            gridTemplateColumns: ["1fr", "1fr 1fr"],
          }}>
            {fieldLoaiLienHe}
            {fieldNgayHen}
            {fieldKetQua}
            {fieldHocSinh}
          </Box>
          <Box sx={{ px: 3, pb: 3 }}>{fieldGhiChu}</Box>

          <Box sx={{
            px: 3, py: 2, bg: "canvas.subtle", borderBottomLeftRadius: 2, borderBottomRightRadius: 2,
            borderTop: "1px solid", borderColor: "border.muted",
            display: "flex", alignItems: "center", gap: 2,
          }}>
            {formState.isLoading && (
              <Text sx={{ fontSize: 0, color: "attention.fg" }}>Đang xử lý...</Text>
            )}
            <Box sx={{ ml: "auto", display: "flex", gap: 2 }}>
              <Button text="Hủy" onClick={resetForm} disabled={formState.isLoading} />
              <Button text="Lưu" variant="primary" isLoading={formState.isLoading} onClick={handleSubmit} />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CSKHForm;
